/**
 * Generic Input component that will display based on the type.
 *
 * This is used by Field.
 */

import React, { useMemo } from "react";
import { hasAndIsNotEmpty } from "../../utils/helpers";
import InputText, { InputTextProps } from "../InputText";
import InputCheckbox, { InputCheckboxProps } from "../InputCheckbox";
import InputRadio, { InputRadioProps } from "../InputRadio";
import InputRadioGroup, { InputRadioGroupProps } from "../InputRadioGroup";
import InputSelect, { InputSelectProps } from "../InputSelect";
import InputSelectCustom, {
  InputSelectCustomProps
} from "../InputSelectCustom";
import InputTags, { InputTagsProps } from "../InputTags";
import InputYearMonthDay, {
  InputYearMonthDayProps
} from "../InputYearMonthDay";
import InputDateOfBirth, { InputDateOfBirthProps } from "../InputDateOfBirth";
import InputSignature, { InputSignatureProps } from "../InputSignature";
import InputPhone, { InputPhoneProps } from "../InputPhone";
import InputTitle, { InputTitleProps } from "../InputTitle";
import InputCustomPicklist from "../InputCustomPicklist";
import InputCountry, { InputCountryProps } from "../InputCountry";
import InputCountryRegion, {
  InputCountryRegionProps
} from "../InputCountryRegion";
import InputTextAutocompleteGoogleMaps, {
  InputTextAutoCompleteGoogleMapsProps
} from "../InputTextAutocompleteGoogleMaps";
import InputTextAutocompleteDqe, {
  InputTextAutoCompleteDqeProps
} from "../InputTextAutocompleteDqe";
import InputPhoneDqe, { InputPhoneDqeProps } from "../InputPhoneDqe";
import InputEmailDqe, { InputEmailDqeProps } from "../InputEmailDqe";
import InputCpfField, { InputCpfProps } from "../InputCpfField";
import InputTax, { InputTaxProps } from "../InputTax";

export interface InputElementProps {
  [propKey: string]: any;
}

export interface InputProps {
  type: string;
  id: string;
  name: string;
  value?: any;
  inputProps?: InputElementProps;
  onCustomError?: (error: string) => void;
  onClearCustomError?: () => void;
  onSetLable?: (text: string) => void;
  onClaerCustomLabel?: () => void;
}

export default function Input({
  type = "text",
  id,
  name,
  value,
  inputProps = {},
  onCustomError,
  onClearCustomError,
  onSetLable,
  onClaerCustomLabel
}: InputProps) {
  // Create a class name that corresponds to the field's name
  const _inputClassName = useMemo(() => `input-control input-name-${name}`, [
    name
  ]);

  // Include the above class name with any className value passed in the
  // `inputProps` object
  const _inputProps = useMemo(
    () => ({
      ...inputProps,
      className: hasAndIsNotEmpty(inputProps, "className")
        ? `${inputProps.className} ${_inputClassName}`
        : _inputClassName
    }),
    [_inputClassName, inputProps]
  );

  // Output the right component
  switch (type) {
    // This will overwrite native `type="date"` inputs
    case "date":
    case "InputYearMonthDay":
      return (
        <InputYearMonthDay
          {...(_inputProps as InputYearMonthDayProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "dob":
    case "date-birth":
    case "birthDate":
    case "dateOfBirth":
    case "InputDateOfBirth":
      return (
        <InputDateOfBirth
          {...(_inputProps as InputDateOfBirthProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "checkbox":
    case "InputCheckbox":
    case "toggleable":
    case "InputToggleable":
      return (
        <InputCheckbox
          {...(_inputProps as InputCheckboxProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "radio":
    case "InputRadio":
      return (
        <InputRadio
          {...(_inputProps as InputRadioProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "radioGroup":
    case "InputRadioGroup":
      return (
        <InputRadioGroup
          {...(_inputProps as InputRadioGroupProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "dropdown":
    case "select":
    case "InputSelect":
      return (
        <InputSelect
          {...(_inputProps as InputSelectProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "dropdown-custom":
    case "dropdownCustom":
    case "select-custom":
    case "selectCustom":
    case "InputSelectCustom":
      return (
        <InputSelectCustom
          {...(_inputProps as InputSelectCustomProps)}
          id={id}
          name={name}
          value={value}
        />
      );
    case "tags":
      return (
        <InputTags
          {...(_inputProps as InputTagsProps)}
          id={id}
          name={name}
          value={value}
        />
      );
    case "signature":
    case "InputSignature":
      return (
        <InputSignature
          {...(_inputProps as InputSignatureProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "phone":
      return (
        <InputPhone
          {...(_inputProps as InputPhoneProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "PhoneDqe":
      return (
        <InputPhoneDqe
          {...(_inputProps as InputPhoneDqeProps)}
          id={id}
          name={name}
          value={value}
          onCustomError={onCustomError}
          onClearCustomError={onClearCustomError}
          onCustomLable={onSetLable}
          onClaerCustomLabel={onClaerCustomLabel}
        />
      );

    case "emailDqe":
      return (
        <InputEmailDqe
          {...(_inputProps as InputEmailDqeProps)}
          id={id}
          name={name}
          value={value}
          onCustomError={onCustomError}
          onClearCustomError={onClearCustomError}
          onCustomLable={onSetLable}
          onClaerCustomLabel={onClaerCustomLabel}
        />
      );

    case "country":
    case "addressCountry":
    case "InputCountry":
      return (
        <InputCountry
          {...(_inputProps as InputCountryProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "region":
    case "country-region":
    case "countryRegion":
    case "addressRegion":
    case "InputCountryRegion":
      return (
        <InputCountryRegion
          {...(_inputProps as InputCountryRegionProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "title":
    case "honorificPrefix":
    case "InputTitle":
      return (
        <InputTitle
          {...(_inputProps as InputTitleProps)}
          id={id}
          name={name}
          value={value}
        />
      );
    case "customPicklist":
      return (
        <InputCustomPicklist
          {...(_inputProps as InputTitleProps)}
          id={id}
          name={name}
          value={value}
        />
      );

    case "textAutocompleteGoogleMaps":
      if (window.google) {
        return (
          <InputTextAutocompleteGoogleMaps
            {...(_inputProps as InputTextAutoCompleteGoogleMapsProps)}
            id={id}
            name={name}
            value={value}
          />
        );
      } else {
        return (
          <InputText
            {...(_inputProps as InputTextProps)}
            type={type}
            id={id}
            name={name}
            value={value}
          />
        );
      }

    case "textAutocompleteDqe":
      return (
        <InputTextAutocompleteDqe
          {...(_inputProps as InputTextAutoCompleteDqeProps)}
          id={id}
          name={name}
          value={value}
        />
      );
    case "InputCpfField":
      return (
        <InputCpfField
          {...(_inputProps as InputCpfProps)}
          id={id}
          name={name}
          value={value}
        />
      );
    case "tax":
      return (
        <InputTax
          {...(_inputProps as InputTaxProps)}
          id={id}
          name={name}
          value={value}
        />
      );
    case "textarea":
    default:
      return (
        <InputText
          {...(_inputProps as InputTextProps)}
          type={type}
          id={id}
          name={name}
          value={value}
          onCustomError={onCustomError}
          onClearCustomError={onClearCustomError}
        />
      );
  }
}
